import {ReactNode, createContext, useCallback, useContext, useEffect} from 'react';
import {useNavigate, useParams} from 'react-router';

import {brandT} from '@/lib/types/brand.types';
import {distributorT} from '@/lib/types/distributor.types';
import {marketT} from '@/lib/types/market.types';

import {defaultCreateData} from '@/lib/helpers';
import {SupabaseInstance} from '@/lib/utils';

import {useItemQuery} from '@/hooks/useItemQuery';

import {useAuth} from '@/providers/components/authProvider';
import {useDistributors} from '@/providers/components/distributorsProvider';

/* ******************************************************************
 * DistributorContext
 ****************************************************************** */
type DistributorContextT = {
  // Properties
  distributor: distributorT | undefined;
  isLoading: boolean;
  error: Error | null;
  distributorId: number | undefined;
  // Functions
  refreshDistributor: () => Promise<void>;
  updateDistributor: (distributor: distributorT) => Promise<distributorT>;
  deleteDistributor: () => Promise<void>;
  searchMarketsToAdd: (name: string) => Promise<marketT[]>;
  addMarketToDistributor: (market_id: number) => Promise<void>;
  removeMarketFromDistributor: (market_id: number) => Promise<void>;
  searchBrandsToAdd: (name: string) => Promise<brandT[]>;
  addBrandToDistributor: (brand_id: number) => Promise<void>;
  removeBrandFromDistributor: (brand_id: number) => Promise<void>;
};

const DistributorContext = createContext({} as DistributorContextT);

/* ******************************************************************
 * DistributorProvider
 ***************************************************** ************* */
type DistributorProviderPropsT = {
  children: ReactNode;
};

/**
 * Provides the distributor object to the children components and useDistributor custom hook. Gets the distributor
 * by ID from the database and navigates back to the distributor list if there's an error. The ID for the
 * distributor is plucked from the URL params. If there's no valid ID, the component will navigate back to the
 * distributor list.
 *
 * **NOTE** - This component is designed to be controlled via the URL params. There is no way to set the
 * distributor that is being fetched manually. Example: /admin/distributors/:distributorId - so if the distributorId is
 * not present in the URL, the component will navigate back to the distributor list.
 *
 * @param {DistributorProviderPropsT} props - The component props.
 * @returns {Context<DistributorContextT>} - The distributor provider component.
 */
export const DistributorProvider = ({children}: DistributorProviderPropsT) => {
  const navigate = useNavigate();
  const {isAdministrator} = useAuth();
  const {id: distributorIdFromParams} = useParams();
  const {updateData: updateDistributorMutation, deleteData: deleteDistributorMutation} = useDistributors();

  // Constants
  const distributorId = Number(distributorIdFromParams);

  // TanStack Query hooks
  const {
    data: fetchedDistributor,
    isLoading,
    error,
    refetch,
  } = useItemQuery<'Distributor'>({table: 'Distributor', id: distributorId});

  // Functions
  /*
   * Gets a distributor records from the database.
   */
  const getDistributor = useCallback(async () => {
    if (!isAdministrator) throw new Error('Error getting retailers');
    try {
      const {data} = await refetch();
      const _distributor = data ?? null;
      return _distributor;
    } catch (error) {
      console.error('Error in getRetailer:', error);
      return undefined;
    }
  }, [isAdministrator, refetch]);

  /*
   * Refreshes the retailer data from the database.
   */
  const refreshDistributor = async () => {
    await getDistributor();
  };

  /*
   * Updates the retailer in the database.
   */
  const updateDistributor = async (distributor: distributorT) => {
    if (!isAdministrator) throw new Error('Error updating distributor');

    try {
      const updatedDistributor = await updateDistributorMutation(distributor);
      return updatedDistributor;
    } catch (error) {
      console.error('Error in updateRetailer:', error);
      throw error;
    }
  };

  /*
   * Deletes the retailer from the database.
   */
  const deleteDistributor = async () => {
    if (!isAdministrator) throw new Error('Error deleting distributor');

    // We must have a retailer to delete
    if (!fetchedDistributor) throw new Error('Error deleting distributor');

    try {
      await deleteDistributorMutation(fetchedDistributor.distributor_id);
      navigate('/admin/distributors');
    } catch (error) {
      console.error('Error in deleteDistributor:', error);
      throw error;
    }
  };

  /*
   * Searchs for Markets to add to the distributor
   */
  const searchMarketsToAdd = async (name: string) => {
    try {
      const query = SupabaseInstance.supabase.from('Market').select().ilike('market_name', `%${name}%`);

      // Execute the query
      const {data: markets, error: marketError} = await query;

      if (marketError) {
        console.error('Error in searchMarketsToAdd:', marketError);
        throw new Error(marketError.message);
      }

      return markets;
    } catch (error) {
      console.error('Error searching markets:', error);
      throw error;
    }
  };

  /*
   * Adds a market to the distributor
   */
  const addMarketToDistributor = async (marketId: number) => {
    try {
      const createData = await defaultCreateData();
      const data = {
        ...createData,
        fk_market_id: marketId,
        fk_distributor_id: distributorId,
        date_removed: null,
        removed_by: null,
      };
      const {error} = await SupabaseInstance.supabase.from('Market_Distributor').upsert(data, {
        onConflict: 'fk_distributor_id,fk_market_id',
      });

      if (error) throw new Error(error?.message || 'Error adding market to distributor');
    } catch (error) {
      throw new Error((error as Error).message);
    }
  };

  /*
   * Removes a market from the distributor
   */
  const removeMarketFromDistributor = async (marketId: number) => {
    try {
      const {data: user} = await SupabaseInstance.supabase.auth.getUser();
      const {error} = await SupabaseInstance.supabase
        .from('Market_Distributor')
        .update({date_removed: new Date().toISOString(), removed_by: user.user?.id})
        .eq('fk_market_id', marketId)
        .eq('fk_distributor_id', distributorId);

      if (error) throw new Error(error?.message || 'Error removing market from distributor');
    } catch (error) {
      throw new Error((error as Error).message);
    }
  };

  /*
   * Search for brands to add to the distributor
   */
  const searchBrandsToAdd = async (name: string) => {
    try {
      const query = SupabaseInstance.supabase.from('Brand').select().ilike('brand_name', `%${name}%`);

      // Execute the query
      const {data: brands, error: brandError} = await query;

      if (brandError) {
        console.error('Error in searchBrandsToAdd:', brandError);
        throw new Error(brandError.message);
      }

      return brands;
    } catch (error) {
      console.error('Error searching brands:', error);
      throw error;
    }
  };

  /*
   * Adds a brand to the distributor
   */
  const addBrandToDistributor = async (brandId: number) => {
    try {
      const createData = await defaultCreateData();
      const data = {
        ...createData,
        fk_brand_id: brandId,
        fk_distributor_id: distributorId,
        date_removed: null,
        removed_by: null,
      };
      const {error} = await SupabaseInstance.supabase.from('Distributor_Brand').upsert(data, {
        onConflict: 'fk_distributor_id,fk_brand_id',
      });

      if (error) throw new Error(error?.message || 'Error adding brand to distributor');
    } catch (error) {
      throw new Error((error as Error).message);
    }
  };

  /*
   * Removes a brand from the distributor
   */
  const removeBrandFromDistributor = async (brandId: number) => {
    try {
      const {data: user} = await SupabaseInstance.supabase.auth.getUser();
      const {error} = await SupabaseInstance.supabase
        .from('Distributor_Brand')
        .update({date_removed: new Date().toISOString(), removed_by: user.user?.id})
        .eq('fk_brand_id', brandId)
        .eq('fk_distributor_id', distributorId);

      if (error) throw new Error(error?.message || 'Error removing brand from distributor');
    } catch (error) {
      throw new Error((error as Error).message);
    }
  };

  /*******************************************************************
   * Configuration
   ********************************************************************/
  useEffect(() => {
    // If there's an error getting the retailer, navigate back to the retailer list
    if (error || !distributorId) {
      console.error(error);
      navigate('/admin/distributors');
    }
  }, [error, navigate, distributorId, fetchedDistributor]);

  return (
    <DistributorContext.Provider
      value={{
        distributor: fetchedDistributor,
        distributorId: fetchedDistributor?.distributor_id,
        isLoading,
        error,
        refreshDistributor,
        updateDistributor,
        deleteDistributor,
        searchMarketsToAdd,
        addMarketToDistributor,
        removeMarketFromDistributor,
        searchBrandsToAdd,
        addBrandToDistributor,
        removeBrandFromDistributor,
      }}
    >
      {children}
    </DistributorContext.Provider>
  );
};

/* ******************************************************************
 * useDistributor Hook
 ****************************************************************** */
export const useDistributor = () => {
  const context = useContext(DistributorContext);
  if (context && Object.keys(context).length === 0 && Object.getPrototypeOf(context) === Object.prototype) {
    throw new Error('useDistributor must be used within a DistributorProvider');
  }

  return context;
};
